import React, { useState, useEffect } from 'react';
import ReusableTable from '../components/adminTable';
import { TableColumn, TableAction } from '../components/adminTable';
import { ArrowRightLeft, Users, CheckCircle, XCircle } from 'lucide-react';
import { useNotification } from '../../sharedComponents/Notifications';
import Drawer from '../../sharedComponents/Drawer';
import Pagination from '../components/Pagination';
import { Modal, MultiSelect } from '../components/adminModals';
import {
    journalsByDeskService,
    submissionsByDeskService,
    submissionService,
    Submission,
    DeskItem,
    SubmissionStatus,
    JournalAccessorDropdownItem,
} from '../services/adminAuth';
import { useAuth } from '../../../auth/hooks/auth';
import AdminSubmissionDetails from '../components/AdminSubmissionDetails';
import { formatRoleLabel } from '../../../utils/roleLabel';

// ─── Status helper — API returns strings ("Submitted", "UnderReview", …) ──────
const STATUS_STRING_TO_NUMBER: Record<string, number> = {
    Submitted:   SubmissionStatus.Submitted,
    UnderReview: SubmissionStatus.UnderReview,
    Returned:    SubmissionStatus.Returned,
    Rejected:    SubmissionStatus.Rejected,
    Approved:    SubmissionStatus.Approved,
};

function resolveStatus(status: number | string): number {
    if (typeof status === 'number') return status;
    return STATUS_STRING_TO_NUMBER[status] ?? SubmissionStatus.Submitted;
}

// ─────────────────────────────────────────────────────────────────────────────

function JournalsByDesk() {
    const { showNotification } = useNotification();
    const { user } = useAuth();

    const isJournalConvener = user?.role === 'journalconvener';
    const isDess = user?.role === 'dess';

    // Desk owner label for the page header (e.g. "Journals by DESS's Desk")
    const deskRoleLabel = isJournalConvener ? 'Journal Convener'
        : isDess ? 'DESS'
        : formatRoleLabel(user?.displayRole);

    const [journals, setJournals] = useState<Submission[]>([]);
    const [loading, setLoading] = useState(true);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Search
    const [search, setSearch] = useState('');

    // Details drawer
    const [selectedJournal, setSelectedJournal] = useState<Submission | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    // Move modal
    const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
    const [moveJournal, setMoveJournal] = useState<Submission | null>(null);
    const [deskList, setDeskList] = useState<DeskItem[]>([]);
    const [deskListLoading, setDeskListLoading] = useState(false);
    const [selectedDeskId, setSelectedDeskId] = useState<number | ''>('');
    const [moveComment, setMoveComment] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    // Move to author modal (DESS)
    const [isCommunicateStatusOpen, setIsCommunicateStatusOpen] = useState(false);
    const [communicateTarget, setCommunicateTarget] = useState<Submission | null>(null);
    const [communicateStatus, setCommunicateStatus] = useState<number | ''>('');
    const [communicateComment, setCommunicateComment] = useState('');
    const [isCommunicating, setIsCommunicating] = useState(false);

    // Assign panel modal
    const [isAssignPanelOpen, setIsAssignPanelOpen] = useState(false);
    const [assignPanelTarget, setAssignPanelTarget] = useState<Submission | null>(null);
    const [journalMembers, setJournalMembers] = useState<JournalAccessorDropdownItem[]>([]);
    const [fetchingMembers, setFetchingMembers] = useState(false);
    const [selectedMemberIds, setSelectedMemberIds] = useState<(string | number)[]>([]);
    const [isAssigning, setIsAssigning] = useState(false);

    // ── Fetch ──────────────────────────────────────────────────────────────────

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchJournals();
        }, 400);
        return () => clearTimeout(handler);
    }, [currentPage, pageSize, search]);

    const fetchJournals = async () => {
        setLoading(true);
        try {
            const response = await journalsByDeskService.getJournalsByDesk({
                pageNumber: currentPage,
                pageSize,
                sortBy: 'SubmittedAt',
                sortDescending: true,
                search,
                categoryId: 1,
            });
            if (response.isSuccess && response.data) {
                setJournals(response.data.data);
                setTotalRecords(response.data.totalRecords);
                setTotalPages(response.data.totalPages);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to fetch journals' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while fetching journals' });
        } finally {
            setLoading(false);
        }
    };

    // ── Move modal ─────────────────────────────────────────────────────────────

    const openMoveModal = async (journal: Submission) => {
        setMoveJournal(journal);
        setSelectedDeskId('');
        setMoveComment('');
        setIsMoveModalOpen(true);
        setDeskListLoading(true);
        try {
            const response = await journalsByDeskService.getJournalDeskList();
            setDeskList(response.isSuccess && response.data ? response.data : []);
        } catch {
            showNotification('error', { title: 'Error', message: 'Failed to load desk list' });
        } finally {
            setDeskListLoading(false);
        }
    };

    const closeMoveModal = () => {
        setIsMoveModalOpen(false);
        setMoveJournal(null);
        setSelectedDeskId('');
        setMoveComment('');
    };

    const handleMoveJournal = async () => {
        if (!moveJournal || selectedDeskId === '') return;
        setIsMoving(true);
        try {
            const response = await submissionsByDeskService.moveSubmission({
                submissionId: moveJournal.id,
                comment: moveComment,
                toWorkflowStage: selectedDeskId as number,
            });
            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Journal moved successfully' });
                closeMoveModal();
                fetchJournals();
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to move journal' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An error occurred while moving the journal' });
        } finally {
            setIsMoving(false);
        }
    };

    // ── Move to Author modal (DESS) ────────────────────────────────────────────

    // Replace openMoveToAuthorModal with:
    const openCommunicateStatusModal = (journal: Submission) => {
        setCommunicateTarget(journal);
        setCommunicateStatus('');
        setCommunicateComment('');
        setIsCommunicateStatusOpen(true);
    };

    // Remove or update close function:
    const closeCommunicateStatusModal = () => {
        setIsCommunicateStatusOpen(false);
        setCommunicateTarget(null);
        setCommunicateStatus('');
        setCommunicateComment('');
    };

    // Replace handleMoveToAuthor with:
    const handleCommunicateStatus = async () => {
        if (!communicateTarget || communicateStatus === '') return;
        setIsCommunicating(true);
        try {
            // Use communicateSubmissionStatusToAuthor instead of moveSubmissionToAuthor
            const response = await submissionService.communicateSubmissionStatusToAuthor({
                submissionId: communicateTarget.id,
                comment: communicateComment.trim() || '',
                submissionStatus: communicateStatus as number,
            });
            if (response.isSuccess) {
                showNotification('success', { 
                    title: 'Success', 
                    message: response.message || 'Status communicated to author successfully.' 
                });
                closeCommunicateStatusModal();
                fetchJournals();
            } else {
                showNotification('error', { 
                    title: 'Error', 
                    message: response.message || 'Failed to communicate status to author.' 
                });
            }
        } catch {
            showNotification('error', { 
                title: 'Error', 
                message: 'An unexpected error occurred.' 
            });
        } finally {
            setIsCommunicating(false);
        }
    };

    // ── Assign panel modal ─────────────────────────────────────────────────────

    const openAssignPanelModal = async (journal: Submission) => {
        setAssignPanelTarget(journal);
        setSelectedMemberIds([]);
        setIsAssignPanelOpen(true);
        setFetchingMembers(true);
        try {
            const response = await journalsByDeskService.getJournalAccessors();
            if (response.isSuccess && response.data) {
                setJournalMembers(response.data);
            } else {
                showNotification('error', { title: 'Error', message: response.message || 'Failed to load journal members' });
                setJournalMembers([]);
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred while loading members' });
            setJournalMembers([]);
        } finally {
            setFetchingMembers(false);
        }
    };

    const closeAssignPanelModal = () => {
        setIsAssignPanelOpen(false);
        setAssignPanelTarget(null);
        setSelectedMemberIds([]);
        setJournalMembers([]);
    };

    const handleAssignPanel = async () => {
        // Strict 3-member panel rule (kept for reference, temporarily relaxed — any 1+ allowed):
        // if (!assignPanelTarget || selectedMemberIds.length < 3) return;
        if (!assignPanelTarget || selectedMemberIds.length < 1) return;
        setIsAssigning(true);
        try {
            const assignedMembers = selectedMemberIds.map(id => {
                const member = journalMembers.find(m => m.id === id);
                return { memberId: id.toString(), name: member?.name ?? '' };
            });

            const response = await journalsByDeskService.assignMembersForJournalAssessment({
                submissionId: assignPanelTarget.id,
                assignedMembers,
            });

            if (response.isSuccess) {
                showNotification('success', { title: 'Success', message: 'Journal assessment panel assigned successfully' });
                closeAssignPanelModal();
                fetchJournals();
            } else {
                showNotification('error', { title: 'Assignment Failed', message: response.message || 'Failed to assign panel' });
            }
        } catch {
            showNotification('error', { title: 'Error', message: 'An unexpected error occurred during assignment' });
        } finally {
            setIsAssigning(false);
        }
    };

    // ── Helpers ────────────────────────────────────────────────────────────────

    const formatDate = (dateString: string) =>
        new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: '2-digit',
        });

    const getStatusBadge = (status: number | string) => {
        const resolved = resolveStatus(status);
        const statusMap: { [key: number]: { label: string; className: string } } = {
            [SubmissionStatus.Submitted]:   { label: 'Submitted',    className: 'bg-gray-100 text-gray-700' },
            [SubmissionStatus.UnderReview]: { label: 'Under Review', className: 'bg-blue-50 text-blue-700' },
            [SubmissionStatus.Returned]:    { label: 'Returned',     className: 'bg-yellow-50 text-yellow-700' },
            [SubmissionStatus.Rejected]:    { label: 'Rejected',     className: 'bg-red-50 text-red-700' },
            [SubmissionStatus.Approved]:    { label: 'Approved',     className: 'bg-green-50 text-green-700' },
        };
        const config = statusMap[resolved] ?? statusMap[SubmissionStatus.Submitted];
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${config.className}`}>
                {config.label}
            </span>
        );
    };

    // ── Columns ────────────────────────────────────────────────────────────────

    const columns: TableColumn<Submission>[] = [
        {
            key: 'submissionNumber',
            label: 'ID',
            render: (value) => (
                <span className="text-gray-500 text-sm font-mono">{value}</span>
            ),
        },
        {
            key: 'title',
            label: 'Title / Rep',
            render: (value, row) => {
                const repName = (row as any).author?.person?.fullName as string | undefined;
                const position = (row as any).positionOfJournalRepresentative as string | undefined;
                return (
                    <div>
                        <div className="font-medium text-gray-900 mb-0.5">{row.title}</div>
                        {repName && (
                            <div className="text-sm text-gray-500">
                                {repName}
                                {position && (
                                    <span className="text-gray-400"> ({position})</span>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'journalProfessionalAssociation' as keyof Submission,
            label: 'Journal Body',
            render: (value, row) => {
                const assoc = (row as any).journalProfessionalAssociation as string | undefined;
                return (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                        {assoc || '—'}
                    </span>
                );
            },
        },
        {
            key: 'submittedAt',
            label: 'Date Submitted',
            render: (value) => (
                <span className="text-sm text-gray-700">{formatDate(value)}</span>
            ),
        },
        {
            key: 'status',
            label: 'Status',
            render: (value) => getStatusBadge(value as number | string),
        },
        {
            key: 'plagiarismOutcome',
            label: 'Similarity',
            render: (value, row) => (
                <span className={`font-semibold ${
                    row.plagiarismOutcome >= 25 ? 'text-red-600'
                    : row.plagiarismOutcome >= 15 ? 'text-yellow-600'
                    : 'text-green-600'
                }`}>
                    {row.plagiarismOutcome}%
                </span>
            ),
        },
        // {
        //     key: 'status',
        //     label: 'Accessed?',
        //     render: (_, row) => {
        //         const resolved = resolveStatus(row.status);
        //         const isUnderReview = resolved === SubmissionStatus.UnderReview;
        //         return isUnderReview ? (
        //             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
        //                 <CheckCircle className="w-3.5 h-3.5" />
        //                 Yes
        //             </span>
        //         ) : (
        //             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
        //                 <XCircle className="w-3.5 h-3.5" />
        //                 No
        //             </span>
        //         );
        //     },
        // },
        {
            key: 'completedPrimaFacie',
            label: 'Journal Assessment Completed?',
            render: (value, row) => (
                <div className="flex flex-col items-start gap-1">
                    {row.completedPrimaFacie ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Yes
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-50 text-red-600 border border-red-200">
                            <XCircle className="w-3.5 h-3.5" />
                            No
                        </span>
                    )}
                    {row.primaFacieDecision && (() => {
                        const isRecommended = !row.primaFacieDecision!.toLowerCase().includes('not');
                        return (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${
                                isRecommended
                                    ? 'bg-green-50 text-green-700 border border-green-100'
                                    : 'bg-red-50 text-red-700 border border-red-100'
                            }`}>
                                {row.primaFacieDecision}
                            </span>
                        );
                    })()}
                </div>
            ),
        },
    ];

    // ── Actions ────────────────────────────────────────────────────────────────

    const actions: TableAction<Submission>[] = [
        {
            variant: 'dropdown',
            onClick: () => undefined,
            dropdownItems: [
                {
                    label: 'View Details',
                    onClick: (row) => {
                        setSelectedJournal(row);
                        setIsDetailsOpen(true);
                    },
                    className: 'text-blue-600',
                },
                {
                    label: 'Move Journal',
                    onClick: openMoveModal,
                    className: isJournalConvener ? 'hidden' : isDess ? 'hidden' : 'text-blue-600',
                },
                {
                    label: 'Assign Journal Assessment Panel',
                    onClick: openAssignPanelModal,
                    className: isJournalConvener ? 'text-blue-600' : 'hidden',
                },
                {
                    label: 'Communicate Decision to Author',  // Changed from 'Move Journal to Author'
                    onClick: openCommunicateStatusModal,      // Changed function name
                    className: isDess ? 'text-[#088A61] font-semibold' : 'hidden',
                },
            ],
            title: 'Actions',
        },
    ];

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="w-full">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    {deskRoleLabel ? `Journals by ${deskRoleLabel}'s Desk` : 'Journals by Desk'}
                </h1>
                <p className="text-gray-500 text-sm sm:text-base">
                    View journal submissions currently at your desk for review and processing.
                </p>
            </div>

            {/* Table */}
            <ReusableTable
                title="Desk Journals"
                subTitle="Journal submissions currently assigned to your desk"
                columns={columns}
                data={journals}
                actions={actions}
                loading={loading}
                searchPlaceholder="Search by title, author, or ID..."
                onSearch={(q) => { setSearch(q); setCurrentPage(1); }}
                emptyMessage="No journal submissions found at your desk."
                showSearch={true}
                showFilter={false}
            />

            {/* Pagination */}
            {!loading && totalRecords > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    pageSize={pageSize}
                    onPageChange={(p) => setCurrentPage(p)}
                    onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
                />
            )}

            {/* ── Move Journal Modal ─────────────────────────────────────────────── */}
            <Modal
                isOpen={isMoveModalOpen}
                onClose={closeMoveModal}
                title="Move Journal"
                description="You're about to move this journal to another desk. Please confirm the destination and optionally leave a comment."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {moveJournal && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{moveJournal.title}</p>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Move To <span className="text-red-500">*</span>
                        </label>
                        {deskListLoading ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading desks...
                            </div>
                        ) : (
                            <select
                                value={selectedDeskId}
                                onChange={(e) => setSelectedDeskId(e.target.value === '' ? '' : Number(e.target.value))}
                                className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                            >
                                <option value="">Select destination desk...</option>
                                {deskList.map((desk) => (
                                    <option key={desk.id} value={desk.id}>{desk.name}</option>
                                ))}
                            </select>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={moveComment}
                            onChange={(e) => setMoveComment(e.target.value)}
                            placeholder="Add a note about this move..."
                            rows={3}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeMoveModal}
                            disabled={isMoving}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleMoveJournal}
                            disabled={isMoving || selectedDeskId === ''}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isMoving ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Moving...
                                </>
                            ) : (
                                <>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Move Journal
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Communicate Decision to Author Modal (DESS) ───────────────────────── */}
            <Modal
                isOpen={isCommunicateStatusOpen}
                onClose={closeCommunicateStatusModal}
                title="Communicate Decision to Author"
                description="Communicate a funding/publication decision back to the author."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {communicateTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{communicateTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{communicateTarget.submissionNumber}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Decision <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={communicateStatus}
                            onChange={(e) => setCommunicateStatus(e.target.value === '' ? '' : Number(e.target.value))}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                        >
                            <option value="">Select decision...</option>
                            <option value={SubmissionStatus.Approved}>Acceptable for Funding</option>
                            <option value={SubmissionStatus.Rejected}>Not Acceptable for Funding</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Comment <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={communicateComment}
                            onChange={(e) => setCommunicateComment(e.target.value)}
                            placeholder="Add a comment regarding this decision..."
                            rows={4}
                            className="block w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent resize-none"
                        />
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeCommunicateStatusModal}
                            disabled={isCommunicating}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCommunicateStatus}
                            disabled={communicateStatus === '' || isCommunicating}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isCommunicating ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Communicating...
                                </>
                            ) : (
                                <>
                                    <ArrowRightLeft className="w-4 h-4" />
                                    Communicate Decision
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Assign Journal Assessment Panel Modal ─────────────────────────── */}
            <Modal
                isOpen={isAssignPanelOpen}
                onClose={closeAssignPanelModal}
                title="Assign Journal Assessment Panel"
                description="Select one or more members to form the journal assessment panel for this submission."
                maxWidth="md"
            >
                <div className="space-y-4 mt-2">
                    {assignPanelTarget && (
                        <div className="bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Journal</p>
                            <p className="text-sm font-medium text-gray-900 line-clamp-2">{assignPanelTarget.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{assignPanelTarget.categoryName || assignPanelTarget.category?.name}</p>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Panel Members <span className="text-red-500">*</span>
                            <span className="text-gray-400 font-normal ml-1">(select 1 or more)</span>
                        </label>
                        {fetchingMembers ? (
                            <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading members...
                            </div>
                        ) : journalMembers.length === 0 ? (
                            <div className="w-full px-3 py-3 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-400 text-center italic">
                                No members available
                            </div>
                        ) : (
                            <MultiSelect
                                options={journalMembers.map(m => ({ value: m.id, label: m.name }))}
                                selectedValues={selectedMemberIds}
                                onChange={setSelectedMemberIds}
                                placeholder="Select panel members..."
                            />
                        )}
                        {selectedMemberIds.length > 0 && (
                            <>
                                {/* Strict 3-member hint (kept for reference, temporarily relaxed):
                                <p className={`text-xs mt-1.5 font-medium ${selectedMemberIds.length >= 3 ? 'text-green-700' : 'text-amber-600'}`}>
                                    {selectedMemberIds.length} selected
                                    {selectedMemberIds.length < 3 && ` — select at least ${3 - selectedMemberIds.length} more`}
                                </p> */}
                                <p className="text-xs mt-1.5 font-medium text-green-700">
                                    {selectedMemberIds.length} selected
                                </p>
                            </>
                        )}
                    </div>

                    {selectedMemberIds.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1.5">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Selected Panel</p>
                            {selectedMemberIds.map(id => {
                                const member = journalMembers.find(m => m.id === id);
                                return member ? (
                                    <div key={String(id)} className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-xs flex-shrink-0">
                                            {member.name.charAt(0)}
                                        </div>
                                        <span className="text-sm text-gray-700">{member.name}</span>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-3 pt-2">
                        <button
                            onClick={closeAssignPanelModal}
                            disabled={isAssigning}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAssignPanel}
                            disabled={isAssigning || selectedMemberIds.length === 0 /* was: < 3 (strict 3-member panel) */}
                            className="px-5 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isAssigning ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                    </svg>
                                    Assigning...
                                </>
                            ) : (
                                <>
                                    <Users className="w-4 h-4" />
                                    Assign Panel
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Details Drawer ─────────────────────────────────────────────────── */}
            <Drawer
                isOpen={isDetailsOpen}
                onClose={() => {
                    setIsDetailsOpen(false);
                    setSelectedJournal(null);
                }}
                title="Journal Details"
                width="max-w-3xl"
            >
                {selectedJournal && (
                    <AdminSubmissionDetails submissionId={selectedJournal.id} />
                )}
            </Drawer>
        </div>
    );
}

export default JournalsByDesk;